/**
 * SearchPage Component
 * Cross-user search interface for finding batch record reviews
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, ChevronRight, ChevronDown, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { SessionAuditClient, SessionStatus, BRRApiClient } from '../services';
import { BRRSession } from '../services/types';
import { StatusBadge } from '../components/StatusBadge';
import { ReassignBRRButton } from '../components/ReassignBRRButton';
import { useSearchData, QuickFilter } from '../hooks/useSearchData';

interface SearchPageProps {
  userEmail: string;
  sessionClient: SessionAuditClient;
  apiClient: BRRApiClient;
  onSelectBatch: (batchId: string, sessionId: string, isReadOnly: boolean) => void;
  isActive?: boolean;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50] as const;

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function getProductName(session: BRRSession, columns: any): string {
  const direct = session[columns.productname];
  if (direct) return direct;
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.productName) return parsed.productName;
    }
  } catch { /* ignore */ }
  return '-';
}

function getDetailField(session: BRRSession, columns: any, ...keys: string[]): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      for (const key of keys) {
        if (parsed?.[key]) return parsed[key];
      }
    }
  } catch { /* ignore */ }
  return '-';
}

const SearchPageComponent: React.FC<SearchPageProps> = ({ userEmail, sessionClient, apiClient, onSelectBatch, isActive }) => {
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState('date-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    sessions,
    filteredSessions,
    filters,
    setFilters,
    clearFilters,
    loading,
    error,
    isTruncated,
    refresh,
    distinctProducts,
    distinctReviewers,
    distinctSites,
    distinctCountries,
  } = useSearchData({ sessionClient, userEmail });

  const columns = sessionClient.columns;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  // Quick filter counts
  const quickFilterCounts = useMemo(() => {
    const lower = userEmail.toLowerCase();
    return {
      all: sessions.length,
      'my-brrs': sessions.filter((s) => (s[columns.useremail] || '').toLowerCase() === lower).length,
      'in-progress': sessions.filter((s) => s[columns.status] === SessionStatus.InProgress).length,
      'pending-qp-review': sessions.filter((s) => s[columns.status] === SessionStatus.Submitted).length,
      completed: sessions.filter((s) => s[columns.status] === SessionStatus.Completed).length,
    };
  }, [sessions, columns, userEmail]);

  // Sort
  const sortedSessions = useMemo(() => {
    const sorted = [...filteredSessions];
    sorted.sort((a, b) => {
      let aVal: string, bVal: string;
      switch (sortBy) {
        case 'date-desc':
        case 'date-asc': {
          aVal = a[columns.startedat] || '';
          bVal = b[columns.startedat] || '';
          const cmp = aVal.localeCompare(bVal);
          return sortBy === 'date-desc' ? -cmp : cmp;
        }
        case 'batch-asc':
        case 'batch-desc': {
          aVal = a[columns.batchid] || '';
          bVal = b[columns.batchid] || '';
          const cmp = aVal.localeCompare(bVal);
          return sortBy === 'batch-desc' ? -cmp : cmp;
        }
        case 'status': {
          const aStatus = a[columns.status] ?? 0;
          const bStatus = b[columns.status] ?? 0;
          return aStatus - bStatus;
        }
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredSessions, sortBy, columns]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sortedSessions.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedSessions = sortedSessions.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );

  const handleRowClick = (session: BRRSession) => {
    const batchId = session[columns.batchid] || '';
    const sessionId = session[columns.sessionid] || '';
    const ownerEmail = (session[columns.useremail] || '').toLowerCase();
    const isOwner = ownerEmail === userEmail.toLowerCase();
    onSelectBatch(batchId, sessionId, !isOwner);
  };

  const quickFilters: { id: QuickFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'my-brrs', label: 'My Batch Record Reviews' },
    { id: 'in-progress', label: 'In Progress' },
    { id: 'pending-qp-review', label: 'Pending QP Submission' },
    { id: 'completed', label: 'Completed' },
  ];

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Search Batch Record Reviews</h1>
          <p className="text-sm text-gray-500">Find and filter BRRs across all products and sites</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="px-3 py-2 bg-white border border-gray-200 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded text-sm font-medium transition-colors flex items-center gap-2 ${showFilters ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Truncation Warning */}
      {isTruncated && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle size={16} />
          <span>Showing the most recent 500 sessions. Use filters to narrow your search.</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filters.searchText}
            onChange={(e) => setFilters({ searchText: e.target.value })}
            placeholder="Search by Batch ID, Product, Material, Reviewer..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
          />
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Quick Filters:</span>
        {quickFilters.map((qf) => (
          <button
            key={qf.id}
            onClick={() => setFilters({ quickFilter: qf.id })}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filters.quickFilter === qf.id
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {qf.label} ({quickFilterCounts[qf.id]})
          </button>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Advanced Filters</h3>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SAP Internal Batch Number</label>
              <input
                type="text"
                value={filters.batchNumber}
                onChange={(e) => setFilters({ batchNumber: e.target.value })}
                placeholder="e.g., B-2026-0142"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={filters.product}
                onChange={(e) => setFilters({ product: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 bg-white"
              >
                <option value="">All Products</option>
                {distinctProducts.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CMO / Site</label>
              <select
                value={filters.site}
                onChange={(e) => setFilters({ site: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 bg-white"
              >
                <option value="">All Sites</option>
                {distinctSites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={filters.country}
                onChange={(e) => setFilters({ country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 bg-white"
              >
                <option value="">All Countries</option>
                {distinctCountries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ dateFrom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ dateTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reviewer</label>
              <input
                type="text"
                value={filters.reviewer}
                onChange={(e) => setFilters({ reviewer: e.target.value })}
                placeholder="Reviewer email"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Results Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold text-gray-900">{filteredSessions.length}</span> of <span className="font-semibold text-gray-900">{sessions.length}</span> results
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 bg-white"
          >
            <option value="date-desc">Date (Newest First)</option>
            <option value="date-asc">Date (Oldest First)</option>
            <option value="batch-asc">Batch ID (A-Z)</option>
            <option value="batch-desc">Batch ID (Z-A)</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <Loader2 size={32} className="mx-auto text-green-600 animate-spin mb-4" />
          <p className="text-gray-500">Loading sessions...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-white rounded-lg border border-red-200 shadow-sm p-12 text-center">
          <AlertTriangle size={32} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load sessions</h3>
          <p className="text-gray-500 mb-4">{error.message}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Results Table */}
      {!loading && !error && sortedSessions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Batch ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Inspection Lot(s)</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">CMO / Site</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reviewer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedSessions.map((session) => {
                  const batchId = session[columns.batchid] || '';
                  const sessionId = session[columns.sessionid] || '';
                  const ownerEmail = (session[columns.useremail] || '').toLowerCase();
                  const isOwner = ownerEmail === userEmail.toLowerCase();
                  const toNull = (v: string) => v !== '-' ? v : null;
                  const delayReasonVal = toNull(getDetailField(session, columns, 'delayReason'));
                  const delayCmoNameVal = toNull(getDetailField(session, columns, 'delayCmoName'));
                  const delayDetailsVal = toNull(getDetailField(session, columns, 'delayDetails'));
                  const delayActionsVal = toNull(getDetailField(session, columns, 'delayActions'));

                  return (
                    <tr
                      key={`${batchId}-${sessionId}`}
                      onClick={() => handleRowClick(session)}
                      className="hover:bg-green-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-green-600">{batchId}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{getDetailField(session, columns, 'lotKey')}</span>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]" title={getProductName(session, columns)}>
                        <span className="text-sm text-gray-900 block truncate">{getProductName(session, columns)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={session[columns.status] ?? 0}
                          delayReason={delayReasonVal}
                          delayCmoName={delayCmoNameVal}
                          delayDetails={delayDetailsVal}
                          delayActions={delayActionsVal}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{getDetailField(session, columns, 'cmoName', 'site')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{getDetailField(session, columns, 'country', 'destinationCountry')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{session[columns.useremail] || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{formatDate(session[columns.startedat])}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(session);
                            }}
                            className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1"
                          >
                            View
                            <ChevronRight size={14} />
                          </button>
                          <ReassignBRRButton
                            batchId={batchId}
                            sessionId={sessionId}
                            currentOwnerEmail={session[columns.useremail] || ''}
                            sessionClient={sessionClient}
                            apiClient={apiClient}
                            onReassigned={refresh}
                            distinctReviewers={distinctReviewers}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sortedSessions.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
              <div className="flex items-center gap-2 text-gray-600 whitespace-nowrap">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
                >
                  {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <span>
                  {(safePage - 1) * rowsPerPage + 1}–{Math.min(safePage * rowsPerPage, sortedSessions.length)} of {sortedSessions.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <ChevronDown size={18} className="rotate-90" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <ChevronDown size={18} className="-rotate-90" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && sortedSessions.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <Search size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-500 mb-4">Try adjusting your search criteria or filters</p>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export const SearchPage = React.memo(SearchPageComponent);
